import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { adoptEffect, adoptsAsOverride, adoptTitle, describeAdoptList, type AdoptRequest } from '../../domain/adopt.js';
import { useAdopt } from '../../hooks/useAdopt.js';

/**
 * The one confirmation every adopt goes through: adopting writes master, never an environment,
 * and "adopt from production-eu" reads close enough to "write to production-eu" to say so.
 */
export const confirmAdopt = (requests: AdoptRequest[], onConfirm: () => void) =>
    modals.openConfirmModal({
        title: adoptTitle(requests),
        // Above whatever opened it — the push review is itself a modal, mounted after this one's host.
        zIndex: 400,
        children: (
            <Text size="sm">
                Adopt {describeAdoptList(requests)} from{' '}
                <Text span className="rgs-numeric">
                    {requests[0]!.environment}
                </Text>
                {adoptsAsOverride(requests) ? '' : ' into master'}? {adoptEffect(requests)} No environment is written.
            </Text>
        ),
        labels: { confirm: 'Adopt', cancel: 'Cancel' },
        onConfirm,
    });

/** `useAdopt` behind one confirm: several requests to one environment are confirmed ONCE and run in order. */
export const useConfirmedAdopt = () => {
    const run = useAdopt();
    const adoptMany = (requests: AdoptRequest[]) => {
        if (requests.length === 0) return;
        confirmAdopt(requests, () => void run.adoptMany(requests));
    };
    return { adopt: (request: AdoptRequest) => adoptMany([request]), adoptMany, isPending: run.isPending };
};
